import { AssetContainer } from '@babylonjs/core/assetContainer'
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader'
import { Material } from '@babylonjs/core/Materials/material'
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial'
import { Texture } from '@babylonjs/core/Materials/Textures/texture'
import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { MorphTarget } from '@babylonjs/core/Morph/morphTarget'
import { MorphTargetManager } from '@babylonjs/core/Morph/morphTargetManager'
import '@babylonjs/loaders/glTF/2.0'
import AnimationTimer from '../AnimationTimer'
import { FrameConfiguration } from '../Frame'
import getMaterial, { getDefaultPattern } from '../MaterialLoader'
import { GetUrl, Settings } from '../Settings'
import { TrackException } from '../Telemetry'

class AttachmentPoints extends Map<string, TransformNode> {
	constructor() {
		super()
	}
}

export default class Part extends TransformNode {
	public mirror: boolean = false
	public attachmentPoints = new AttachmentPoints()
	public meshes: AbstractMesh[] = []

	private parentNode: TransformNode = null
	protected container: AssetContainer

	protected loadingFilename: string
	public loadedFilename: string

	private managers: MorphTargetManager[] = []

	protected file: string

	public colliders: AbstractMesh[] = []

	config: FrameConfiguration
	hasErrors: boolean
	errors: string[]

	constructor(name: string, parent: TransformNode, mirror: boolean = false) {
		super(name, parent.getScene())
		this.name = name

		this.mirror = mirror
		this.parentNode = parent

		/*if(mirror) {
            this.scaling = new Vector3(-1, 1, 1)
            this.attachmentPoints.forEach(x => x.scaling = new Vector3(-1, 1, 1))
        }*/

		if (this.parentNode) this.parent = this.parentNode
	}

	get filename(): string {
		throw 'Not implemented'
	}

	get isPrecious(): boolean {
		return false
	}

	get aoFilename(): string {
		return `AO/${this.filename}`
	}

	async load(file: File = null) {
		this.hasErrors = false
		this.errors = []

		const filename = this.filename
		if (!filename || filename == this.loadedFilename) {
			this.loadingFilename = null
			try {
				await this.preloadMaterials()
			} catch (error) {
				TrackException(error, { type: 'PreloadMaterials', filename })
				console.log(error)
				this.hasErrors = true
				this.errors.push(`Preload materials failed`)
			}
			this.setEnabled(filename != null)
			return
		}
		// await new Promise(x => setTimeout(x, 2500)) //Debug load
		try {
			this.loadingFilename = filename

			if (file) {
				;[this.container] = await Promise.all([SceneLoader.LoadAssetContainerAsync('', file), this.preloadMaterials()])
			} else {
				;[this.container] = await Promise.all([SceneLoader.LoadAssetContainerAsync(GetUrl(`${this.filename}.glb`)), this.preloadMaterials()])
			}

			// this.container.materials.forEach(x => x.name = this.filename + "/" + x.name)
			this.container.materials.forEach(x => x.dispose())
			this.loadedFilename = filename
			this.setEnabled(true)
			this.getScene().getMaterialByName('__GLTFLoader._default')?.dispose()

			// const meshesWithFinish = ["BRIDGE", "CLIP", "CROSSBAR", "FRONT", "LOWERRIM", "NOSEPAD", "NOSEPADARM", "PLATE", "TEMPLE", "TEMPLE-DETAIL", "UPPERRIM", "WIRE", "WIRE-DETAIL"]
			// const fixedFinishes = this.container.meshes.filter(x => !meshesWithFinish.includes(x.name) && meshesWithFinish.some(c => x.name.startsWith(c))).map(x => x.name)
			// console.log("TODO:", fixedFinishes)
		} catch (error) {
			TrackException(error, { type: 'LoadAssetContainer', filename })
			this.hasErrors = true
			this.errors.push(`Missing Model: ${filename}`)
		}
	}

	getMeshMaterialName(mesh: AbstractMesh): string {
		throw (`${this.name} - getMeshMaterial not implemented`)
	}

	getAOMaterial(mesh: AbstractMesh, original: Material): Material {
		if (Settings.AO && this.useAO(mesh, original)) {
			const material = (this.getScene().getMaterialByName(this.filename + '/' + original.name) || original.clone(this.filename + '/' + original.name)) as PBRMaterial
			material.ambientTexture = this.getScene().getTextureByName(this.aoFilename) || new Texture(GetUrl(this.aoFilename))
			material.ambientTextureStrength = .7
			material.metadata = { isAO: true }
			return material
		} else {
			return original
		}
	}
	useAO(mesh: AbstractMesh, original: Material) {
		return original.getClassName() == 'PBRMaterial' && !original.name.startsWith('SILICONE/') && !mesh.name.startsWith('LENS')
	}

	async compileMaterials() {
		const meshes = (this.container ? this.container.meshes : this.meshes).filter(x => x.name != '__root__')

		await Promise.allSettled(
			meshes.filter(x => this.getMeshMaterialName(x)).map(async mesh => {
				const material = await getMaterial(this.getScene(), this.getMeshMaterialName(mesh))
				const aoMaterial = this.getAOMaterial(mesh, material)
				return aoMaterial.forceCompilationAsync(mesh)
			})
		)
	}

	assignMaterials() {
		this.meshes.forEach(mesh => {
			const original = this.getScene().getMaterialByName(this.getMeshMaterialName(mesh))
			if (!original) {
				this.hasErrors = true
				this.errors.push(`Missing Material: ${this.getMeshMaterialName(mesh)}`)
				return
			}
			if (original.metadata?.isFallback) {
				console.warn('Fallback Material', original.name)
				this.hasErrors = true
				this.errors.push(`Missing Material: ${this.getMeshMaterialName(mesh)}`)
			}
			mesh.material = this.getAOMaterial(mesh, original)
		})
	}

	async preloadMaterials() {
		if ((this.filename?.startsWith('Fronts') || this.filename?.startsWith('Temples') || this.filename?.startsWith('Bushings') || this.filename?.startsWith('Bridges') || this.filename?.startsWith('Clips') || this.filename?.startsWith('Nosepads/Precious')) && Settings.AO) {
			if (!this.getScene().getTextureByName(this.aoFilename)) {
				try {
					await new Promise((resolve, reject) => {
						const texture = new Texture(GetUrl(this.aoFilename), null, null, false, undefined, undefined, reject)
						texture.name = this.aoFilename
						texture.coordinatesIndex = 1
						texture.onLoadObservable.addOnce(resolve)
					})
				} catch (error) {
					this.errors.push(`Missing AO: ${this.aoFilename}`)
					console.log('MISSING AO', error)
					this.getScene().getTextureByName(this.aoFilename)?.dispose()
					TrackException(error, { type: 'LoadAO', name: this.aoFilename })
					this.hasErrors = true
				}
			}
		}
	}

	getComponentMeshes(componentName: string, finish: string = null) {
		const val = this.getChildMeshes(false, x =>
			(x.parent == this || x.parent.parent == this) && (
				new RegExp(`^${componentName}(-(LEFT|RIGHT))?${finish ? `(-(${finish}))` : ''}$`).test(x.name.trim())
			))
		// console.log(componentName, finish, val)
		return val
	}

	async assignMaterial(mesh: AbstractMesh, color: string) {
		if (color == undefined) return console.warn('UNDEFINED COLOR', mesh)
		const original = this.getScene().getMaterialByName(color) as PBRMaterial
		if (original == undefined) return console.warn('UNDEFINED original', color)
		let material = original
		if (!['TEMPLE-TIP', 'WIRE', 'T4046', 'CLEARLENS', 'NOSEPAD', 'SUN2', 'SUN4', 'SUN6', 'DIAMOND', 'SILICONE'].includes(color.split('/')[0])) {
			if (Settings.AO) material = this.getScene().getMaterialByName(this.filename + '/' + color) as PBRMaterial
			if (Settings.AO && !material) {
				material = original.clone(this.filename + '/' + color)
				Object.keys(material).filter(x => material[x]?.constructor?.name == '_Texture' && x != '_environmentBRDFTexture').forEach(x => {
					material[x].dispose()
					material[x] = original[x]
				})
				material.metadata = { isAO: true }
				material.ambientTextureStrength = .7
				material.ambientTexture = this.getScene().getTextureByName(this.aoFilename) || getDefaultPattern(this.getScene())
			}
		}

		mesh.material = material
	}

	setMorph(value: number) {
		this.managers.map(x => x.getTarget(0).influence = value)
	}

	swap(animated: boolean = false) {
		if (this.hasErrors) this.meshes.map(x => x.dispose())

		if (!this.container) {
			if (!this.filename) this.meshes.forEach(x => x.dispose())
			return
		}
		this.attachmentPoints.forEach(x => x.parent = null)
		const prevChildren = this.getChildren()
		// this.getChildren().forEach(x=>x.dispose())
		const oldMeshes = [...this.meshes]
		this.managers.length = 0

		this.container.addToScene()
		this.container.materials.map(x => x.dispose())
		const root = this.getScene().getMeshByName('__root__') // Troublemaker...

		if (!root) {
			console.warn('root missing', this.name, this.container)
			// throw(this.name)
			this.hasErrors = true
			oldMeshes.forEach(x => x.dispose())
			return
		}
		this.meshes = root.getChildMeshes()
		this.container = null

		root.name = 'NEW ROOT'
		root.parent = this

		const outNodes = root.getChildTransformNodes(false).filter(x => x.name.includes('-OUT'))
		outNodes.map(x => x.name += ':IMPORTED')

		root.getChildren(null, true).map(x => x.parent = this)
		// this.meshes.filter(x=>x.name.includes("SLOT")).forEach(x=>x.dispose())

		root.dispose()

		for (let node of outNodes) {
			const point = this.attachmentPoints.get(node.name.split(':')[0]) // ?? this.attachmentPoints.Default()
			if (!point) {
				console.warn('MISSING:', node.name)
				this.getScene()['debugLayer']?.select(node)
				continue
			}

			point.parent = node.parent
			point.scaling = node.scaling.clone() //

			// point.rotationQuaternion = node.rotationQuaternion.clone()

			if (!animated) {
				point.position = node.position.clone()
				//
				// node.rotationQuaternion.toEulerAngles()
				point.rotation = node.rotationQuaternion.toEulerAngles()

				// point.rotationQuaternion = node.rotationQuaternion.clone()
			} else {
				new AnimationTimer(this.getScene(), { target: point, property: 'position', from: point.position.clone(), to: node.position.clone() })
				new AnimationTimer(this.getScene(), { target: point, property: 'rotation', from: point.rotation.clone(), to: node.rotationQuaternion.toEulerAngles() })
				// new AnimationTimer(this.getScene(), {target: point, property: 'rotationQuaternion', from:point.rotationQuaternion.clone(), to:node.absoluteRotationQuaternion.clone()})
				// new AnimationTimer(this.getScene(), { target: point, property: 'rotationQuaternion', from:point.rotationQuaternion.clone(), to:node.rotationQuaternion.clone()})

				// Animation.CreateAndStartAnimation("Move", point, 'position', 60, .6 * 60, point.position.clone(), node.position.clone(), Animation.ANIMATIONLOOPMODE_CONSTANT)
			}

			// point.rotation = node.rotation.clone()
			// point.scaling = node.scaling.clone()

			// TODO: ANIMATE CLIP OUT

			// point.rotationQuaternion = node.rotationQuaternion.clone()

			// new AnimationTimer(this.getScene(), {target: node, property: 'position', from: point.position, to: })
			// point.rotation = node.rotation.clone()
			// point.setDirection(node.rotation.clone())
		}

		this.attachmentPoints.forEach(x => x.setEnabled(x.parent != null))
		if (this.mirror) {
			this.scaling = new Vector3(-1, 1, 1)
			this.attachmentPoints.forEach(x => x.scaling = new Vector3(-1, 1, 1))
		}

		for (const mesh of this.meshes) {
			const prevMesh = oldMeshes.find(x => animated && x.name == mesh.name && x.getTotalVertices() > 0 && x.getTotalVertices() == mesh.getTotalVertices())
			if (prevMesh) {
				mesh.morphTargetManager = new MorphTargetManager()
				let target = MorphTarget.FromMesh(prevMesh)
				target.influence = 0
				mesh.morphTargetManager.addTarget(target)
				this.managers.push(mesh.morphTargetManager)
			}
		}
		prevChildren.map(x => x.dispose())

		// console.log(this.name, this.managers.length)
		this.managers.forEach(morphTargetManager => {
			morphTargetManager.getTarget(0).influence = 1
			new AnimationTimer(this.getScene(), { target: morphTargetManager.getTarget(0), property: 'influence', from: 1, to: 0 })
		})
		outNodes.map(x => x.dispose())
	}
}
